/**
 * Phase 5A: detail-page left sidebar nav. Six items matching the operator
 * screenshot:
 *   Overview / Strategies / Trades / Time / Execution / Download.
 *
 * Active section is sourced from + written back to `?section=` URL param;
 * caller passes the active key + onChange handler. Items can be disabled
 * (rendered grey, no nav) for callers without scope on a section — e.g.
 * Download is publisher / SuperAdmin only.
 */
import React from 'react';
import {
  BarChart3, Clock, Download, Layers, LayoutDashboard, ListChecks,
} from 'lucide-react';
import type { DetailSection } from '../../types/marketplaceAnalytics';
import { cn } from '../../utils/cn';

interface Props {
  active: DetailSection;
  onChange: (next: DetailSection) => void;
  /** Sections the caller cannot reach (rendered greyed out, no nav). */
  disabledSections?: DetailSection[];
}

const items: Array<{ key: DetailSection; label: string; icon: React.ReactNode }> = [
  { key: 'overview',   label: 'Overview',   icon: <LayoutDashboard className="h-4 w-4" /> },
  { key: 'strategies', label: 'Strategies', icon: <Layers className="h-4 w-4" /> },
  { key: 'trades',     label: 'Trades',     icon: <ListChecks className="h-4 w-4" /> },
  { key: 'time',       label: 'Time',       icon: <Clock className="h-4 w-4" /> },
  { key: 'execution',  label: 'Execution',  icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'download',   label: 'Download',   icon: <Download className="h-4 w-4" /> },
];

const MarketplaceSidebarNav: React.FC<Props> = ({
  active, onChange, disabledSections = [],
}) => {
  return (
    <nav
      aria-label="Detail sections"
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-0.5"
    >
      {items.map(({ key, label, icon }) => {
        const isActive = active === key;
        const isDisabled = disabledSections.includes(key);
        return (
          <button
            key={key}
            type="button"
            disabled={isDisabled}
            onClick={() => { if (!isDisabled && !isActive) onChange(key); }}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left',
              isActive
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : isDisabled
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
            )}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className={cn(
              isActive
                ? 'text-blue-600 dark:text-blue-400'
                : isDisabled
                  ? 'text-gray-400 dark:text-gray-600'
                  : 'text-gray-500 dark:text-gray-400',
            )}>
              {icon}
            </span>
            {label}
          </button>
        );
      })}
    </nav>
  );
};

export default MarketplaceSidebarNav;
